/**
 * CR500: CTAButtonBlock
 * 
 * Renders the "Claim deal" call-to-action button.
 * Extracted from DealGiftCard for v2 block-based rendering.
 */

import React from "react";
import { ExternalLink } from "lucide-react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

interface CTAButtonConfig {
  /** Button label text */
  label?: string;
  /** Button variant */
  variant?: "primary" | "secondary" | "outline";
  /** Show external link icon */
  showIcon?: boolean;
}

export const CTAButtonBlock: React.FC<DealCardBlockProps<Deal>> = ({
  deal,
  block,
  context,
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  // Get URL - prioritize claim_url, then fall back to proof_urls
  const actionUrl = deal.claim_url || deal.proof_urls?.[0];
  
  // If no URL, don't render the block
  if (!actionUrl) {
    return null;
  }

  // Block config
  const config = (block.config || {}) as CTAButtonConfig;
  const label = config.label || (deal.claim_url ? "Claim deal" : "View deal");
  const variant = config.variant || "primary";
  const showIcon = config.showIcon !== false;

  // Variant styles using design tokens
  const variantStyles = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90",
    secondary: "bg-foreground text-background hover:bg-foreground/90",
    outline: "bg-muted text-foreground hover:bg-muted/80 border border-border",
  };

  return (
    <div 
      className="mt-4 flex flex-wrap gap-2"
      data-block="cta_button"
      data-block-id={block.id}
    >
      <a
        href={actionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "rounded-full px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5",
          variantStyles[variant]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        {showIcon && <ExternalLink className="w-3.5 h-3.5" />}
      </a>
    </div>
  );
};

export default CTAButtonBlock;
